/**
 * Tab stops for text the browser lays out (the contentEditable while a box is edited, plain HTML otherwise).
 *
 * CSS `tab-size` only gives a uniform grid, so every tab character becomes an inline-block span whose width is measured
 * after layout: the span starts wherever the text before it ends and is widened up to the next tab stop — the box's
 * custom stops (ruler / Paragraph ▸ Tabs) first, then the default grid — with centre / right / decimal stops positioning
 * the field that follows. This is the same rule the line-layout engine (textLayout.js) applies to text that flows around
 * pictures, so a box looks the same while it is edited, once editing ends and in exports.
 *
 * Stop positions are box px from the left edge of the text area; `unit` converts them to the element's local px
 * (display: the canvas zoom; the editable is rendered at box scale with CSS zoom, so unit = 1 / autofit scale).
 */
/** Publisher's default tab grid: 0.5 in = 48 px (kept equal to textLayout.DEFAULT_TAB_STOP; not imported to avoid a module cycle). */
const DEFAULT_TAB_STOP = 48;

export const TAB_ATTR = 'data-wp-tab';
export const TAB_SELECTOR = `span[${TAB_ATTR}]`;
/** Set on a tab span that breaks the line (no stop left before the right edge); index.css puts a newline before it. */
export const WRAP_ATTR = 'data-wrap';
const BLOCK = /^(DIV|P|LI|TD|TH|H[1-6])$/;

export const htmlHasTabs = (html) => typeof html === 'string' && html.includes('\t');

/** A tab span. `editable` spans are atomic (contenteditable=false) so the caret steps over them and Backspace removes them whole. */
export const createTabSpan = (editable = false, doc = document) => {
  const span = doc.createElement('span');
  span.setAttribute(TAB_ATTR, '1');
  if (editable) span.setAttribute('contenteditable', 'false');
  span.textContent = '\t';
  span.style.display = 'inline-block';
  span.style.whiteSpace = 'pre';
  span.style.width = '0px';
  span.style.verticalAlign = 'baseline';
  return span;
};

/** Character offset of (node, offset) inside root — counts the text of every node, tab spans included. */
const charOffset = (root, node, offset) => {
  const r = document.createRange();
  r.selectNodeContents(root);
  try { r.setEnd(node, offset); } catch { return null; }
  return r.toString().length;
};

/** Place the caret `chars` characters into root (never inside a tab span: before / after it instead). */
const placeCaret = (root, chars) => {
  const sel = window.getSelection();
  if (!sel) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let acc = 0;
  let node;
  const range = document.createRange();
  while ((node = walker.nextNode())) {
    const len = node.nodeValue.length;
    if (acc + len >= chars) {
      const tab = node.parentElement?.closest(TAB_SELECTOR);
      if (tab) {
        if (chars - acc <= 0) range.setStartBefore(tab); else range.setStartAfter(tab);
      } else range.setStart(node, chars - acc);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      return;
    }
    acc += len;
  }
  range.selectNodeContents(root);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
};

/**
 * Turn raw tab characters in text nodes under `root` into tab spans. Returns true when anything changed.
 * With `keepCaret`, the live caret / selection end inside root is restored afterwards (typing / pasting a tab).
 */
export const materializeTabs = (root, { editable = false, keepCaret = false } = {}) => {
  if (!root) return false;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => (n.nodeValue.includes('\t') && !n.parentElement?.closest(TAB_SELECTOR) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT),
  });
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  if (!nodes.length) return false;
  let caret = null;
  if (keepCaret) {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && root.contains(sel.focusNode)) caret = charOffset(root, sel.focusNode, sel.focusOffset);
  }
  const doc = root.ownerDocument || document;
  nodes.forEach((t) => {
    const parts = t.nodeValue.split('\t');
    const frag = doc.createDocumentFragment();
    parts.forEach((p, i) => {
      if (i > 0) frag.appendChild(createTabSpan(editable, doc));
      if (p) frag.appendChild(doc.createTextNode(p));
    });
    t.replaceWith(frag);
  });
  if (caret !== null) placeCaret(root, caret);
  return true;
};

/** Replace tab spans by plain tab characters (what gets stored). Works on a detached clone — never on the live editable. */
export const dematerializeTabs = (root) => {
  if (!root) return;
  root.querySelectorAll(TAB_SELECTOR).forEach((s) => s.replaceWith((root.ownerDocument || document).createTextNode('\t')));
  root.normalize();
};

/** HTML with tab spans turned back into tab characters. */
export const htmlWithoutTabSpans = (html) => {
  if (!html || !html.includes(TAB_ATTR)) return html || '';
  const div = document.createElement('div');
  div.innerHTML = html;
  dematerializeTabs(div);
  return div.innerHTML;
};

/** Next tab stop strictly to the right of `x` (local px): custom stops first, then the default grid (`def: true`). */
const nextTabStop = (x, stops, def) => {
  for (const s of stops) if (s.pos > x + 0.5) return s;
  return { pos: (Math.floor((x + 0.5) / def) + 1) * def, align: 'left', leader: 'none', def: true };
};

const closestBlock = (el, root) => {
  let n = el.parentElement;
  while (n && n !== root && !BLOCK.test(n.tagName)) n = n.parentElement;
  return n || root;
};

/** Width (local px) of the text after a tab span up to the next tab span / end of its paragraph; decimal = up to the separator. */
const fieldWidth = (span, root, k, decimal) => {
  const block = closestBlock(span, root);
  const range = document.createRange();
  range.setStartAfter(span);
  range.setEnd(block, block.childNodes.length);
  // end at the next tab in the same paragraph
  const tabs = block.querySelectorAll(TAB_SELECTOR);
  let passed = false;
  for (const t of tabs) {
    if (t === span) { passed = true; continue; }
    if (passed) { range.setEndBefore(t); break; }
  }
  if (decimal) {
    const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (range.comparePoint(node, 0) < 0) continue;
      if (range.comparePoint(node, 0) > 0) break;
      const i = node.nodeValue.search(/[.,]/);
      if (i >= 0) { range.setEnd(node, i); break; }
    }
  }
  let w = 0;
  const rects = range.getClientRects();
  for (const r of rects) w += r.width;
  // trailing spaces do not count (the field ends at the last glyph)
  return Math.max(0, w / k);
};

/**
 * Width (local px) of the first word after a tab span: the text up to the next space, line break, tab or paragraph end.
 * Publisher keeps a tab together with the word that follows it — when the word does not fit after the stop, the line
 * breaks *before* the tab (see layoutTabs).
 */
const BREAK_CHAR = /[ \t\n\r\f\v\u200b]/;
const firstWordWidth = (span, root, k) => {
  const block = closestBlock(span, root);
  const range = document.createRange();
  range.setStartAfter(span);
  range.setEndAfter(span);
  const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
    acceptNode: (n) => {
      if (n.nodeType === Node.ELEMENT_NODE) return n.tagName === 'BR' || n.matches(TAB_SELECTOR) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  walker.currentNode = span;
  let node;
  while ((node = walker.nextNode())) {
    if (span.contains(node)) continue; // the span's own tab character
    if (node.nodeType === Node.ELEMENT_NODE) break; // <br> or the next tab
    const text = node.nodeValue;
    const i = text.search(BREAK_CHAR);
    if (i === 0) break;
    if (i > 0) { range.setEnd(node, i); break; }
    range.setEnd(node, text.length);
  }
  let w = 0;
  for (const r of range.getClientRects()) w += r.width;
  return w / k;
};

/**
 * Where a tab begins, in root-local px: the left edge of the span — or, for a tab that has been made to break the line
 * (`data-wrap`), the end of the text before it on the previous line, i.e. where the tab *would* have started. Measuring
 * it there keeps the break decision stable from pass to pass (the span itself now sits at the start of the next line).
 */
const tabStartX = (sp, root, rootRect, k, padL) => {
  if (!sp.hasAttribute(WRAP_ATTR)) return (sp.getBoundingClientRect().left - rootRect.left) / k;
  const block = closestBlock(sp, root);
  const range = document.createRange();
  range.setStart(block, 0);
  try { range.setEndBefore(sp); } catch { return padL; }
  const rects = range.getClientRects();
  if (!rects.length) return padL;
  const last = rects[rects.length - 1];
  return (last.right - rootRect.left) / k;
};

/** A tab that breaks the line: inline (so the `::before` newline in index.css can break before it) with the tab glyph advancing to `w`. */
const setWrapMode = (sp, w) => {
  if (!sp.hasAttribute(WRAP_ATTR)) sp.setAttribute(WRAP_ATTR, '1');
  if (sp.style.display !== 'inline') sp.style.display = 'inline';
  if (sp.style.width !== 'auto') sp.style.width = 'auto';
  const ts = `${Math.round(Math.max(0, w) * 100) / 100}px`;
  if (sp.style.tabSize !== ts) sp.style.tabSize = ts;
};
const setInlineMode = (sp, w) => {
  if (sp.hasAttribute(WRAP_ATTR)) sp.removeAttribute(WRAP_ATTR);
  if (sp.style.display !== 'inline-block') sp.style.display = 'inline-block';
  if (sp.style.tabSize) sp.style.tabSize = '';
  const ws = `${Math.round(w * 100) / 100}px`;
  if (sp.style.width !== ws) sp.style.width = ws;
};

/**
 * Size every tab span under `root` (the text element that carries the paragraph indents as padding).
 * Positions are measured from root's left edge. Publisher's rules, which this follows (checked against a real
 * publication whose hanging "Leader: / People:" blocks are built from a tab typed at the start of every line):
 *  - a **default** tab stop only counts while a whole default interval (½ in) is left after it — a tab typed in the
 *    last half inch of a line does not creep to a stop a few pixels away; a **custom** stop counts anywhere before the edge;
 *  - **a tab sticks to the word after it**: when no stop is left, or the stop is reached but the word does not fit on
 *    the line, the line **breaks before the tab**, which lands on the first tab stop of the next line and the word
 *    follows it there — "…swarms of living<tab>creatures" shows "creatures" indented on the next line.
 * Only when even that first stop lies beyond the edge (a very narrow box) is the tab dropped. The former "fill the rest of
 * the line" sizing made the span end exactly at the edge, so whether the word stayed on the line or wrapped came down to
 * sub-pixel rounding — and an export could lay the same text out differently from the screen.
 * Runs measure / write passes until the widths are stable (each tab only depends on the text before it).
 */
export const layoutTabs = (root, { stops = [], defaultTab = DEFAULT_TAB_STOP, unit = 1 } = {}) => {
  if (!root) return false;
  const spans = Array.from(root.querySelectorAll(TAB_SELECTOR));
  if (!spans.length) return false;
  const u = unit > 0 ? unit : 1;
  const custom = (stops || []).filter((s) => s && Number.isFinite(s.pos)).map((s) => ({ ...s, pos: s.pos * u })).sort((a, b) => a.pos - b.pos);
  const def = (defaultTab > 1 ? defaultTab : DEFAULT_TAB_STOP) * u;
  const cs = getComputedStyle(root);
  const padL = parseFloat(cs.paddingLeft) || 0;
  const padR = parseFloat(cs.paddingRight) || 0;
  const right = root.offsetWidth - padR;
  // the first stop of a fresh line — where a line-breaking tab puts the text that follows it
  const first = nextTabStop(padL, custom, def);
  const firstW = first.pos > right + 0.5 ? 0 : first.pos - padL;
  // No stop left before the edge: a default stop needs a whole interval after it (unless the tab starts the line); a
  // custom left stop within a pixel of the edge is as good as none (a span sized to end exactly there may or may not
  // fit — the knife edge that made screen and export disagree); centred / right / decimal stops may sit on the edge.
  const beyond = (stop, lineStart) => {
    if (lineStart) return stop.pos > right + 0.5;
    if (stop.def) return stop.pos + def > right + 0.5;
    return stop.align && stop.align !== 'left' ? stop.pos > right + 0.5 : stop.pos > right - 1;
  };
  for (let pass = 0; pass < 12; pass++) {
    const rootRect = root.getBoundingClientRect();
    const k = root.offsetWidth > 0 && rootRect.width > 0 ? rootRect.width / root.offsetWidth : 1; // viewport px per local px (CSS zoom)
    const plan = spans.map((sp) => {
      const x = tabStartX(sp, root, rootRect, k, padL);
      const lineStart = x <= padL + 1; // nothing before it on the line: never breaks (that would leave an empty line)
      const stop = nextTabStop(x, custom, def);
      if (beyond(stop, lineStart)) {
        if (lineStart || firstW <= 0) return { w: 0, wrap: false, leader: 'none' }; // nothing before it and no stop in reach (or a box too narrow for any stop): dropped
        return { w: firstW, wrap: true, leader: 'none' }; // breaks the line; the text after it starts at the first stop
      }
      let w = stop.pos - x;
      let fitted = false; // a centred / right / decimal field positioned around its stop fits by construction
      if (stop.align && stop.align !== 'left') {
        const fw = fieldWidth(sp, root, k, stop.align === 'decimal');
        const adj = stop.align === 'center' ? fw / 2 : fw;
        // the field ends half a pixel short of the stop so a hair's width of difference in the glyph advances (screen
        // vs export raster) cannot push it onto the next line
        if (w - adj >= 0) { w = Math.max(0, w - adj - 0.5); fitted = true; } // a field too wide for an aligned stop behaves like a left stop
      }
      // the word after the tab must fit on this line too; a pixel of margin keeps the browser's own break decision on
      // the same side as ours
      if (!lineStart && !fitted && firstW > 0 && x + w + firstWordWidth(sp, root, k) > right - 1) return { w: firstW, wrap: true, leader: 'none' };
      return { w: Math.max(0, w), wrap: false, leader: stop.leader || 'none' };
    });
    let changed = false;
    spans.forEach((sp, i) => {
      const { w, wrap, leader } = plan[i];
      const wasWrap = sp.hasAttribute(WRAP_ATTR);
      const cur = wasWrap ? parseFloat(sp.style.tabSize) || 0 : parseFloat(sp.style.width) || 0;
      if (wasWrap !== wrap || Math.abs(cur - w) > 0.5) {
        if (wrap) setWrapMode(sp, w); else setInlineMode(sp, w);
        changed = true;
      }
      if (leader !== 'none') { if (sp.getAttribute('data-leader') !== leader) sp.setAttribute('data-leader', leader); }
      else if (sp.hasAttribute('data-leader')) sp.removeAttribute('data-leader');
    });
    if (!changed) break;
  }
  return true;
};

/** Tab options for a text box's props (merged with defaults). */
export const tabOptionsOf = (props, unit = 1) => ({ stops: props.tabStops || [], defaultTab: props.defaultTabStop || DEFAULT_TAB_STOP, unit });
/** Stable key for memo / effect dependencies. */
export const tabOptionsKey = (opts) => `${opts.unit}|${opts.defaultTab}|${(opts.stops || []).map((s) => `${s.pos}:${s.align || 'left'}:${s.leader || 'none'}`).join(',')}`;
